import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactElement,
  type SetStateAction
} from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AddressReadOnly } from '../../AddressFieldsGroup';
import { ProcurementRequestEditor } from '../forms/ProcurementRequestEditor';
import { RequestPoCreateSection } from './RequestPoCreateSection';
import { RfqWorkflowPanel } from './RfqWorkflowPanel';
import {
  findAvailabilityForMaterial,
  RequestLineStockAvailability,
  useRequestInventoryAvailability
} from './RequestLineStockAvailability';
import { RequestLinkedMaterialTransfers } from './RequestLinkedMaterialTransfers';
import { PagerSection, SectionPager } from '../layout/SectionPager';
import type {
  LoginResponse,
  ProcurementRequest,
  PurchaseOrder,
  PoFormState,
  RegionMasterDto,
  RequestFormData,
  DeliveryAddressDirectoryEntry,
  VendorDirectoryEntry,
  UpdatePurchaseOrderPayload
} from '../../domain/types';
import type { RequestForQuoteDetail } from '../../domain/types';
import type { WellMaster } from '../../domain/wells';
import {
  findRegionField,
  materialLineDetailRows,
  materialsSummary,
  requestDisplayTitle
} from '../../domain/types';
import { activityFromRequestLog } from '../../domain/activityLog';
import { materialInventoryNeed } from '../../domain/procurementRequest';
import { ActivityTable } from '../activity/ActivityTable';
import {
  formatMoney,
  formatPoStatus,
  formatStatus,
  requestDetailHeading,
  canPerformProcurementStaffActions,
  isReadOnlyRole,
  parseNonNegativeUnitPrice,
  parseOptionalUsdAmount,
  purchaseOrderGrandTotal,
  purchaseOrderLineItemsSubtotal
} from '../../domain/formatters';
import { regionDisplayName, toWellRegionFieldInfo, type WellRegionFieldInfo } from '../../domain/wellRegions';
import { normalizeRfqStatus } from '../../domain/requestForQuote';

export interface RequestDetailSidePanelProps {
  selectedRequest: ProcurementRequest;
  auth: LoginResponse;
  canManageSelectedDraft: boolean;
  draftEditForm: RequestFormData;
  setDraftEditForm: Dispatch<SetStateAction<RequestFormData>>;
  wells: WellMaster[];
  activeRegions: RegionMasterDto[];
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  panelComment: string;
  setPanelComment: (v: string) => void;
  onAddRequestNote: (body: string) => void | Promise<void>;
  purchaseOrder: PurchaseOrder | null;
  poForm: PoFormState;
  setPoForm: Dispatch<SetStateAction<PoFormState>>;
  createPurchaseOrder: (forProcurementRequestId?: string) => void | Promise<void>;
  vendors: VendorDirectoryEntry[];
  onReloadVendors: () => void | Promise<void>;
  deliveryAddresses: DeliveryAddressDirectoryEntry[];
  onClose: () => void;
  onExportRequestPdf: () => void;
  onExportPoPdf: (po: PurchaseOrder) => void;
  onUpdateDraft: () => void;
  onSubmitDraftForApproval: () => void;
  onDeleteDraft: () => void;
  onRegionalDecision: (id: string, approve: boolean) => void;
  onQueueForProcurement: (id: string) => void;
  onRejectProcurement: (id: string, comment?: string) => void | Promise<void>;
  onUpdatePurchaseOrder?: (id: string, payload: UpdatePurchaseOrderPayload) => void | Promise<void>;
  onMarkPoPaid: (id: string) => void;
  onMarkProcured: (id: string) => void;
  onMarkComplete: (id: string) => void;
  /** Full-page layout (double-click from grid) uses back navigation instead of a slide-out close control. */
  variant?: 'panel' | 'page';
  onBack?: () => void;
  /** IT Support: hide all mutating controls (API also enforces read-only). */
  readOnly?: boolean;
  /** Failed submit-for-approval validation keys (draft editor). */
  draftSubmitFieldErrors?: readonly string[];
  /** Latest request for quote for this procurement request (sourcing). */
  rfqDetail: RequestForQuoteDetail | null;
  rfqLoading: boolean;
  onCreateRfq: () => void | Promise<void>;
  onUpdateRfq: (
    id: string,
    payload: { requirements: string; incotermsSnapshot: string; vendorIds: string[] }
  ) => void | Promise<void>;
  onSendRfqInvitations: (id: string) => void | Promise<void>;
  onSelectVendorForRfq: (id: string, vendorId: string) => void | Promise<void>;
  onCancelRfq: (id: string) => void | Promise<void>;
  canApproveRfqWorkflow: boolean;
  onApproveRfq: (id: string, comment?: string) => void | Promise<void>;
  onRejectRfq: (id: string, comment?: string) => void | Promise<void>;
  /** From sourcing RFQ: open the RFQ registry full-page route. */
  onOpenRfqPage?: (rfqId: string) => void;
  /** Open the linked procurement request in the app (e.g. full-page request URL). */
  onOpenProcurementRequest?: (procurementRequestId: string) => void;
  /** When false, RFQ workflow is hidden; manual PO from procurement review (or legacy sourcing) instead. */
  sourcingEnabled?: boolean;
}

export function RequestDetailSidePanel({
  selectedRequest,
  auth,
  canManageSelectedDraft,
  draftEditForm,
  setDraftEditForm,
  wells,
  activeRegions,
  authFetch,
  panelComment,
  setPanelComment,
  onAddRequestNote,
  purchaseOrder,
  poForm,
  setPoForm,
  createPurchaseOrder,
  vendors,
  onReloadVendors,
  deliveryAddresses,
  onClose,
  onExportRequestPdf,
  onExportPoPdf,
  onUpdateDraft,
  onSubmitDraftForApproval,
  onDeleteDraft,
  onRegionalDecision,
  onQueueForProcurement,
  onRejectProcurement,
  onUpdatePurchaseOrder,
  onMarkPoPaid,
  onMarkProcured,
  onMarkComplete,
  variant = 'panel',
  onBack,
  readOnly = false,
  draftSubmitFieldErrors,
  rfqDetail,
  rfqLoading,
  onCreateRfq,
  onUpdateRfq,
  onSendRfqInvitations,
  onSelectVendorForRfq,
  onCancelRfq,
  canApproveRfqWorkflow,
  onApproveRfq,
  onRejectRfq,
  onOpenRfqPage,
  onOpenProcurementRequest,
  sourcingEnabled = true
}: RequestDetailSidePanelProps): ReactElement {
  const isPage = variant === 'page';
  const ro = readOnly || isReadOnlyRole(auth.role);
  const sourcingOn = sourcingEnabled;
  const materialTransfersEnabled = auth.profile?.materialTransfersEnabled ?? true;
  const showStockAvailability =
    canPerformProcurementStaffActions(auth.role) &&
    (selectedRequest.status === 'procurementReview' || selectedRequest.status === 'sourcing');
  const showTransferCta = showStockAvailability && !ro && materialTransfersEnabled;
  const requestRegion =
    selectedRequest.wellInfo?.region?.trim() || selectedRequest.requestor?.region?.trim() || '';
  const stockAvailability = useRequestInventoryAvailability(
    selectedRequest.id,
    selectedRequest.materials,
    authFetch,
    showStockAvailability
  );
  const [newNoteDraft, setNewNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const [procurementRejectComment, setProcurementRejectComment] = useState('');
  const [procurementRejectSaving, setProcurementRejectSaving] = useState(false);

  const [poFeeFreight, setPoFeeFreight] = useState('');
  const [poFeeMisc, setPoFeeMisc] = useState('');
  const [poLineUnitInputs, setPoLineUnitInputs] = useState<string[]>([]);
  const [poPricingSaving, setPoPricingSaving] = useState(false);
  const [poPricingEditing, setPoPricingEditing] = useState(false);

  const poLineSnapshot = useMemo(
    () =>
      purchaseOrder
        ? purchaseOrder.lineItems.map((l) => `${l.unitCost}:${l.quantity}:${l.lineTotal}`).join(';')
        : '',
    [purchaseOrder]
  );

  useEffect(() => {
    setNewNoteDraft('');
  }, [selectedRequest.id]);

  useEffect(() => {
    setPoPricingEditing(false);
  }, [purchaseOrder?.id]);

  function syncPoPricingFromPurchaseOrder(po: PurchaseOrder): void {
    const f = po.freightAmount ?? 0;
    const m = po.miscellaneousFeeAmount ?? 0;
    setPoFeeFreight(f !== 0 ? String(f) : '');
    setPoFeeMisc(m !== 0 ? String(m) : '');
    setPoLineUnitInputs(po.lineItems.map((l) => String(l.unitCost)));
  }

  useEffect(() => {
    if (!purchaseOrder) {
      setPoFeeFreight('');
      setPoFeeMisc('');
      setPoLineUnitInputs([]);
      return;
    }
    if (poPricingEditing) {
      return;
    }
    syncPoPricingFromPurchaseOrder(purchaseOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when PO id/fees/lines change; not while editing
  }, [purchaseOrder?.id, purchaseOrder?.freightAmount, purchaseOrder?.miscellaneousFeeAmount, poLineSnapshot, poPricingEditing]);

  const requestDeliveryAddress = deliveryAddresses.find((d) => d.id === selectedRequest.deliveryAddressId);

  const catalogField = useMemo(() => {
    const code =
      selectedRequest.wellInfo?.region?.trim() || selectedRequest.requestor?.region?.trim() || '';
    const fid = selectedRequest.regionFieldInfo?.id?.trim() || '';
    return findRegionField(activeRegions, code, fid);
  }, [selectedRequest.wellInfo?.region, selectedRequest.requestor?.region, selectedRequest.regionFieldInfo?.id, activeRegions]);

  const resolvedWellFieldInfo = useMemo((): WellRegionFieldInfo | undefined => {
    const fromCatalog = catalogField ? toWellRegionFieldInfo(catalogField) : undefined;
    if (fromCatalog) {
      return fromCatalog;
    }
    const rf = selectedRequest.regionFieldInfo;
    const name = rf?.name?.trim() || selectedRequest.wellInfo?.field?.trim() || '';
    const county = rf?.county?.trim() || '';
    const state = rf?.state?.trim() || '';
    if (!name && !county && !state && !rf?.id?.trim()) {
      return undefined;
    }
    return {
      id: rf?.id?.trim() || '',
      fieldName: name || '—',
      county,
      state,
      closestCity: rf?.closestCity?.trim() || '',
      milesFromCity: rf?.milesFromCity ?? undefined
    };
  }, [catalogField, selectedRequest.regionFieldInfo, selectedRequest.wellInfo?.field]);

  const sortedCollaborationNotes = useMemo(() => {
    const list = selectedRequest.collaborationNotes ?? [];
    return [...list].sort(
      (a, b) => new Date(a.createdAtUtc).getTime() - new Date(b.createdAtUtc).getTime()
    );
  }, [selectedRequest.collaborationNotes]);

  const sortedActivityLog = useMemo(() => {
    const list = selectedRequest.activityLog ?? [];
    return [...list].sort((a, b) => new Date(a.atUtc).getTime() - new Date(b.atUtc).getTime());
  }, [selectedRequest.activityLog]);

  async function handleAddNote(): Promise<void> {
    const text = newNoteDraft.trim();
    if (!text || noteSaving) {
      return;
    }
    setNoteSaving(true);
    try {
      await onAddRequestNote(text);
      setNewNoteDraft('');
    } finally {
      setNoteSaving(false);
    }
  }

  function beginEditPoPricing(): void {
    if (purchaseOrder) {
      syncPoPricingFromPurchaseOrder(purchaseOrder);
    }
    setPoPricingEditing(true);
  }

  function cancelEditPoPricing(): void {
    if (purchaseOrder) {
      syncPoPricingFromPurchaseOrder(purchaseOrder);
    }
    setPoPricingEditing(false);
  }

  function setPoLineUnitAt(index: number, value: string): void {
    setPoLineUnitInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  async function savePoPricing(): Promise<void> {
    if (!purchaseOrder || !onUpdatePurchaseOrder) {
      return;
    }
    const fp = parseOptionalUsdAmount(poFeeFreight);
    const mp = parseOptionalUsdAmount(poFeeMisc);
    if (!fp.ok || !mp.ok) {
      let errMsg = 'Invalid amount.';
      if (!fp.ok) {
        errMsg = fp.message;
      } else if (!mp.ok) {
        errMsg = mp.message;
      }
      toast.error(errMsg);
      return;
    }

    const unitCosts: number[] = [];
    for (let i = 0; i < purchaseOrder.lineItems.length; i++) {
      const raw = poLineUnitInputs[i] ?? '';
      const up = parseNonNegativeUnitPrice(raw);
      if (!up.ok) {
        toast.error(
          purchaseOrder.lineItems.length > 1 ? `Line ${i + 1}: ${up.message}` : up.message
        );
        return;
      }
      unitCosts.push(up.amount);
    }

    const payload: UpdatePurchaseOrderPayload = {
      freightAmount: fp.amount,
      miscellaneousFeeAmount: mp.amount,
      lineItemUnitCosts: unitCosts
    };

    setPoPricingSaving(true);
    try {
      await onUpdatePurchaseOrder(purchaseOrder.id, payload);
      setPoPricingEditing(false);
    } finally {
      setPoPricingSaving(false);
    }
  }

  return (
    <aside className={`side-panel${isPage ? ' side-panel--page' : ''}`} aria-label="Request details">
      <div className="side-panel-header">
        <div className="side-panel-title-row">
          {isPage && onBack && (
            <button type="button" className="secondary panel-back-btn" onClick={onBack}>
              ← Back to list
            </button>
          )}
          <h3 data-testid="request-detail-heading">{requestDetailHeading(selectedRequest)}</h3>
          <span className={`status-pill status-${selectedRequest.status}`}>
            {formatStatus(selectedRequest.status)}
          </span>
        </div>
        <div className="side-panel-actions">
          <button type="button" className="secondary panel-export-btn" onClick={onExportRequestPdf}>
            Export PDF
          </button>
          {!isPage && (
            <button type="button" className="icon-close" onClick={onClose} aria-label="Close panel">
              ×
            </button>
          )}
        </div>
      </div>
      <div className="side-panel-body">
        <SectionPager enabled={isPage}>
        <PagerSection id="identity" title="Identity">
        <dl className="detail-list">
          <dt>Request id</dt>
          <dd>{selectedRequest.requestId ?? '—'}</dd>
          <dt>AFE #</dt>
          <dd>{selectedRequest.afeNumber?.trim() || '—'}</dd>
          <dt>Request type</dt>
          <dd>{selectedRequest.requestType}</dd>
          <dt>Well / title</dt>
          <dd>{requestDisplayTitle(selectedRequest)}</dd>
          <dt>Status</dt>
          <dd>
            <span className={`status-pill status-${selectedRequest.status}`}>
              {formatStatus(selectedRequest.status)}
            </span>
          </dd>
          <RequestLinkedMaterialTransfers request={selectedRequest} authFetch={authFetch} />
          <dt>Well API</dt>
          <dd className="mono-id">{selectedRequest.wellInfo?.apiNumber || selectedRequest.wellInfo?.wellId || '—'}</dd>
          <dt>Well name</dt>
          <dd>{selectedRequest.wellInfo?.wellName || '—'}</dd>
        </dl>
        </PagerSection>

        <PagerSection id="field" title="Field">
        {resolvedWellFieldInfo ? (
          <section className="nested-card well-region-field-info-block" aria-label="Region field info">
            <h4 className="form-section-title">Region field info</h4>
            <dl className="detail-list">
              <dt>Field name</dt>
              <dd>{resolvedWellFieldInfo.fieldName?.trim() || '—'}</dd>
              <dt>County</dt>
              <dd>
                {[resolvedWellFieldInfo.county?.trim(), resolvedWellFieldInfo.state?.trim()].filter(Boolean).join(', ') || '—'}
              </dd>
              <dt>Closest city</dt>
              <dd>{resolvedWellFieldInfo.closestCity?.trim() || '—'}</dd>
              <dt>Miles from city</dt>
              <dd>
                {resolvedWellFieldInfo.milesFromCity !== undefined &&
                resolvedWellFieldInfo.milesFromCity !== null &&
                !Number.isNaN(resolvedWellFieldInfo.milesFromCity)
                  ? resolvedWellFieldInfo.milesFromCity
                  : '—'}
              </dd>
            </dl>
          </section>
        ) : null}
        <dl className="detail-list">
          {selectedRequest.deliveryAddressId?.trim() && (
            <>
              <dt>Delivery address</dt>
              <dd>
                {requestDeliveryAddress ? (
                  <>
                    <strong>{requestDeliveryAddress.label}</strong>
                    {requestDeliveryAddress.address ? (
                      <span className="multiline">
                        {' '}
                        — {requestDeliveryAddress.address}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="mono-id">{selectedRequest.deliveryAddressId}</span>
                )}
              </dd>
            </>
          )}
          <dt>Location</dt>
          <dd>
            {[selectedRequest.wellInfo?.location?.state, selectedRequest.wellInfo?.location?.country]
              .filter(Boolean)
              .join(', ') || '—'}
          </dd>
          <dt>Requestor</dt>
          <dd>{selectedRequest.requestor?.name ?? '—'}</dd>
          <dt>Region</dt>
          <dd>
            {(() => {
              const code =
                selectedRequest.wellInfo?.region?.trim() || selectedRequest.requestor?.region?.trim() || '';
              return code ? regionDisplayName(code, activeRegions) : '—';
            })()}
          </dd>
          <dt>Materials summary</dt>
          <dd>{materialsSummary(selectedRequest)}</dd>
        </dl>
        </PagerSection>

        <PagerSection id="materials" title="Materials">
        {selectedRequest.materials.map((mat, i) => (
          <section
            key={`mat-detail-${mat.itemId}-${i}`}
            className="nested-card material-line-readonly rfq-material-card"
            aria-label={`Material line ${i + 1}`}
          >
            <h4 className="form-section-title rfq-material-card-title">
              <span className="rfq-material-line-badge">{i + 1}</span>
              {mat.name?.trim() || `Material line ${i + 1}`}
            </h4>
            <dl className="detail-list rfq-material-detail-list">
              {materialLineDetailRows(mat).map((row, ri) => (
                <Fragment key={`${i}-${ri}-${row.label}`}>
                  <dt>{row.label}</dt>
                  <dd className={row.label === 'Description' ? 'multiline' : undefined}>{row.value}</dd>
                </Fragment>
              ))}
            </dl>
            {showStockAvailability && (
              <RequestLineStockAvailability
                material={mat}
                availability={findAvailabilityForMaterial(stockAvailability.lines, mat)}
                loading={stockAvailability.loading}
                error={stockAvailability.error}
                region={requestRegion}
                afeNumber={selectedRequest.afeNumber ?? ''}
                requestNumber={selectedRequest.requestId ?? selectedRequest.id}
                requestId={selectedRequest.id}
                deliveryAddressId={selectedRequest.deliveryAddressId}
                regionFieldInfo={selectedRequest.regionFieldInfo}
                deliveryAddresses={deliveryAddresses}
                showTransferCta={showTransferCta}
              />
            )}
          </section>
        ))}
        </PagerSection>

        <PagerSection id="justification" title="Justification">
        <dl className="detail-list">
          <dt>Justification</dt>
          <dd className="multiline">{selectedRequest.businessJustification || '—'}</dd>
          {selectedRequest.timeline?.requiredByDate && (
            <>
              <dt>Required by</dt>
              <dd>{selectedRequest.timeline.requiredByDate}</dd>
            </>
          )}
          {selectedRequest.vendorPreferences && selectedRequest.vendorPreferences.preferredVendors.length > 0 && (
            <>
              <dt>Preferred vendors</dt>
              <dd>
                {selectedRequest.vendorPreferences.preferredVendors.map((v) => v.name).join(', ')}
              </dd>
            </>
          )}
          {selectedRequest.attachments && selectedRequest.attachments.length > 0 && (
            <>
              <dt>Attachments</dt>
              <dd>
                <ul className="compact-list">
                  {selectedRequest.attachments.map((a, i) => (
                    <li key={i}>
                      <a href={a.url} target="_blank" rel="noreferrer">
                        {a.fileName}
                      </a>
                    </li>
                  ))}
                </ul>
              </dd>
            </>
          )}
          {selectedRequest.regionalManagerName && (
            <>
              <dt>Regional decision</dt>
              <dd>
                {selectedRequest.regionalManagerName}
                {selectedRequest.regionalDecisionComment ? ` — ${selectedRequest.regionalDecisionComment}` : ''}
              </dd>
            </>
          )}
          {selectedRequest.procurementManagerName && (
            <>
              <dt>Procurement</dt>
              <dd>{selectedRequest.procurementManagerName}</dd>
            </>
          )}
          {selectedRequest.orderPlacedAtUtc && (
            <>
              <dt>Order placed at</dt>
              <dd>{new Date(selectedRequest.orderPlacedAtUtc).toLocaleString()}</dd>
            </>
          )}
          {selectedRequest.procuredAtUtc && (
            <>
              <dt>Procured at</dt>
              <dd>{new Date(selectedRequest.procuredAtUtc).toLocaleString()}</dd>
            </>
          )}
          <dt>Created</dt>
          <dd>{new Date(selectedRequest.createdAtUtc).toLocaleString()}</dd>
        </dl>

        <ActivityTable testId="request-activity" rows={activityFromRequestLog(sortedActivityLog)} />
        </PagerSection>

        <PagerSection id="workflow" title="Workflow">
        {canManageSelectedDraft && !ro && (
          <div className="panel-actions">
            <h4>Edit Draft</h4>
            <div className="grid draft-edit-grid">
              <ProcurementRequestEditor
                formData={draftEditForm}
                setFormData={setDraftEditForm}
                wells={wells}
                activeRegions={activeRegions}
                deliveryAddresses={deliveryAddresses}
                submitFieldErrorKeys={draftSubmitFieldErrors}
                authFetch={authFetch}
              />
            </div>
            <div className="row">
              <button type="button" onClick={() => void onUpdateDraft()}>
                Save draft changes
              </button>
              <button type="button" className="secondary" onClick={() => void onSubmitDraftForApproval()}>
                Submit for approval
              </button>
              <button type="button" className="danger" onClick={() => void onDeleteDraft()}>
                Delete draft
              </button>
            </div>
          </div>
        )}

        {auth.role === 'RegionalManager' && !ro && selectedRequest.status === 'pendingApproval' && (
          <div className="panel-actions">
            <label htmlFor="panel-comment">Comment (optional)</label>
            <textarea
              id="panel-comment"
              value={panelComment}
              onChange={(e) => setPanelComment(e.target.value)}
              placeholder="Add notes for the requestor or procurement…"
            />
            <div className="row">
              <button type="button" onClick={() => void onRegionalDecision(selectedRequest.id, true)}>
                Approve
              </button>
              <button type="button" className="danger" onClick={() => void onRegionalDecision(selectedRequest.id, false)}>
                Reject
              </button>
            </div>
          </div>
        )}

        {purchaseOrder && (
          <div className="panel-actions po-summary">
            <div className="po-summary-header">
              <h4>Purchase order</h4>
              <button type="button" className="secondary panel-export-btn" onClick={() => onExportPoPdf(purchaseOrder)}>
                Export PO PDF
              </button>
            </div>
            <dl className="detail-list compact-po">
              <dt>PO number</dt>
              <dd>{purchaseOrder.purchaseOrderNumber}</dd>
              <dt>PO status</dt>
              <dd>
                <span className={`status-pill po-status-${purchaseOrder.status}`}>
                  {formatPoStatus(purchaseOrder.status)}
                </span>
              </dd>
              <dt>Well</dt>
              <dd>
                {purchaseOrder.wellInfo.wellName?.trim() || '—'}
                {purchaseOrder.wellInfo.field?.trim()
                  ? ` · ${purchaseOrder.wellInfo.field.trim()}`
                  : ''}
              </dd>
              <dt>Supplier</dt>
              <dd>{purchaseOrder.vendorName}</dd>
              <dt>Supplier address</dt>
              <dd>
                <AddressReadOnly value={purchaseOrder.vendorAddress} />
              </dd>
              <dt>Ship to</dt>
              <dd>
                <AddressReadOnly value={purchaseOrder.deliveryAddress} />
              </dd>
              <dt>Incoterms</dt>
              <dd>{purchaseOrder.incoterms}</dd>
              <dt>Created by</dt>
              <dd>{purchaseOrder.createdByName}</dd>
              {purchaseOrder.paidAtUtc && (
                <>
                  <dt>Paid at</dt>
                  <dd>{new Date(purchaseOrder.paidAtUtc).toLocaleString()}</dd>
                </>
              )}
              <dt>Freight</dt>
              <dd>{formatMoney(purchaseOrder.freightAmount ?? 0)}</dd>
              <dt>Miscellaneous fee</dt>
              <dd>{formatMoney(purchaseOrder.miscellaneousFeeAmount ?? 0)}</dd>
              <dt>Lines subtotal</dt>
              <dd>{formatMoney(purchaseOrderLineItemsSubtotal(purchaseOrder))}</dd>
              <dt>Order total</dt>
              <dd>{formatMoney(purchaseOrderGrandTotal(purchaseOrder))}</dd>
            </dl>
            <table className="po-lines">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit price</th>
                  <th>Line total</th>
                </tr>
              </thead>
              <tbody>
                {purchaseOrder.lineItems.map((line, idx) => (
                  <tr key={`${line.partNumber}-${idx}`}>
                    <td>{line.partNumber}</td>
                    <td>{line.description?.trim() || line.equipmentType}</td>
                    <td>{line.quantity}</td>
                    <td>{formatMoney(line.unitCost)}</td>
                    <td>{formatMoney(line.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {canPerformProcurementStaffActions(auth.role) &&
              !ro &&
              purchaseOrder.status === 'created' &&
              onUpdatePurchaseOrder &&
              !poPricingEditing && (
                <div className="panel-actions full-width">
                  <h5 className="po-subheading full-width">Pricing &amp; fees</h5>
                  <p className="hint full-width">
                    Freight {formatMoney(purchaseOrder.freightAmount ?? 0)} · Miscellaneous{' '}
                    {formatMoney(purchaseOrder.miscellaneousFeeAmount ?? 0)}
                  </p>
                  <p className="hint full-width">
                    {purchaseOrder.lineItems.map((line, idx) => (
                      <Fragment key={`${line.partNumber}-${idx}`}>
                        {idx > 0 ? ' · ' : null}
                        {line.partNumber}: {formatMoney(line.unitCost)} × {line.quantity}
                      </Fragment>
                    ))}
                  </p>
                  <div className="row">
                    <button type="button" className="secondary" onClick={beginEditPoPricing}>
                      Edit
                    </button>
                  </div>
                </div>
              )}
            {canPerformProcurementStaffActions(auth.role) &&
              !ro &&
              purchaseOrder.status === 'created' &&
              onUpdatePurchaseOrder &&
              poPricingEditing && (
                <div className="panel-actions full-width po-fees-edit">
                  <h5 className="po-subheading full-width">Edit unit prices, freight &amp; fees</h5>
                  <p className="hint full-width">
                    Update line unit prices (line totals follow quantity). Freight and miscellaneous fee are optional.
                    Click Save when finished.
                  </p>
                  {purchaseOrder.lineItems.map((line, idx) => {
                    const parsed = parseNonNegativeUnitPrice(poLineUnitInputs[idx] ?? '');
                    const unit = parsed.ok ? parsed.amount : 0;
                    const previewTotal = line.quantity * unit;
                    return (
                      <div key={`${line.partNumber}-edit-${idx}`} className="full-width po-line-price-edit">
                        <strong className="po-line-price-edit__label">
                          {line.partNumber} · Qty {line.quantity}
                        </strong>
                        <label className="full-width">
                          Unit price (USD)
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={poLineUnitInputs[idx] ?? ''}
                            onChange={(e) => setPoLineUnitAt(idx, e.target.value)}
                          />
                        </label>
                        <p className="hint">Line total: {formatMoney(previewTotal)}</p>
                      </div>
                    );
                  })}
                  <label className="full-width">
                    Freight (USD)
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={poFeeFreight}
                      onChange={(e) => setPoFeeFreight(e.target.value)}
                      placeholder="0"
                    />
                  </label>
                  <label className="full-width">
                    Miscellaneous fee (USD)
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={poFeeMisc}
                      onChange={(e) => setPoFeeMisc(e.target.value)}
                      placeholder="0"
                    />
                  </label>
                  <div className="row">
                    <button type="button" disabled={poPricingSaving} onClick={() => void savePoPricing()}>
                      {poPricingSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" className="secondary" disabled={poPricingSaving} onClick={cancelEditPoPricing}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            {canPerformProcurementStaffActions(auth.role) && !ro && purchaseOrder.status === 'created' && (
              <div className="panel-actions">
                <button type="button" onClick={() => void onMarkPoPaid(purchaseOrder.id)}>
                  Mark PO paid
                </button>
              </div>
            )}
          </div>
        )}

        {canPerformProcurementStaffActions(auth.role) &&
          (selectedRequest.status === 'procurementReview' || selectedRequest.status === 'sourcing') &&
          selectedRequest.materials.some((m) => materialInventoryNeed(m).fulfilled > 0 && materialInventoryNeed(m).remaining > 0.0001) && (
          <div className="panel-actions">
            <p className="request-stock-availability-hit" role="status">
              Yard stock partially covers this request
              {selectedRequest.fulfilledByMaterialTransferId ? (
                <>
                  {' '}
                  (
                  <Link
                    to={`/material-transfers/${encodeURIComponent(selectedRequest.fulfilledByMaterialTransferId)}/edit`}
                  >
                    {selectedRequest.fulfilledByMaterialTransferNumber ?? 'open transfer'}
                  </Link>
                  )
                </>
              ) : null}
              . Quote the remaining quantity.
            </p>
          </div>
        )}

        {canPerformProcurementStaffActions(auth.role) &&
          selectedRequest.status === 'sourcing' &&
          selectedRequest.sourcingInitiatedByMaterialTransferId && (
          <div className="panel-actions">
            <p className="hint" role="status">
              This request is in sourcing because of a yard transfer.{' '}
              <Link
                to={`/material-transfers/${encodeURIComponent(selectedRequest.sourcingInitiatedByMaterialTransferId)}/edit`}
              >
                Open the transfer
              </Link>{' '}
              and cancel it to return to procurement review if you are not quoting or creating another transfer. Approve
              plans the transfer; a movement ticket completes it.
            </p>
          </div>
        )}

        {canPerformProcurementStaffActions(auth.role) &&
          !ro &&
          sourcingOn &&
          selectedRequest.status === 'procurementReview' && (
          <div className="panel-actions">
            <p className="hint">Move this request into active sourcing.</p>
            <button type="button" onClick={() => void onQueueForProcurement(selectedRequest.id)}>
              Move to sourcing
            </button>
          </div>
        )}

        {canPerformProcurementStaffActions(auth.role) &&
          !ro &&
          !purchaseOrder &&
          !sourcingOn &&
          (selectedRequest.status === 'procurementReview' || selectedRequest.status === 'sourcing') && (
            <RequestPoCreateSection
              selectedRequest={selectedRequest}
              poForm={poForm}
              setPoForm={setPoForm}
              vendors={vendors}
              deliveryAddresses={deliveryAddresses}
              onCreatePurchaseOrder={() => void createPurchaseOrder(selectedRequest.id)}
              onReloadVendors={onReloadVendors}
              authFetch={authFetch}
            />
          )}

        {canPerformProcurementStaffActions(auth.role) && !ro && sourcingOn && selectedRequest.status === 'sourcing' && (
          <RfqWorkflowPanel
            rfqDetail={rfqDetail}
            rfqLoading={rfqLoading}
            vendors={vendors}
            activeRegions={activeRegions}
            onCreateRfq={onCreateRfq}
            onUpdateRfq={onUpdateRfq}
            onSendRfqInvitations={onSendRfqInvitations}
            onSelectVendorForRfq={onSelectVendorForRfq}
            onCancelRfq={onCancelRfq}
            canApproveRfqWorkflow={canApproveRfqWorkflow}
            onApproveRfq={onApproveRfq}
            onRejectRfq={onRejectRfq}
            onOpenFullPage={onOpenRfqPage}
            onOpenProcurementRequest={onOpenProcurementRequest}
            onReloadVendors={onReloadVendors}
            authFetch={authFetch}
          />
        )}

        {canPerformProcurementStaffActions(auth.role) &&
          !ro &&
          sourcingOn &&
          selectedRequest.status === 'sourcing' &&
          rfqDetail &&
          normalizeRfqStatus(rfqDetail.status) === 'approved' && (
            <RequestPoCreateSection
              selectedRequest={selectedRequest}
              poForm={poForm}
              setPoForm={setPoForm}
              vendors={vendors}
              deliveryAddresses={deliveryAddresses}
              onCreatePurchaseOrder={() => void createPurchaseOrder(selectedRequest.id)}
              onReloadVendors={onReloadVendors}
              authFetch={authFetch}
              rfqSelectedVendorId={rfqDetail.selectedVendorId?.trim() ?? null}
              rfqDefaultIncoterms={
                rfqDetail.incotermsSnapshot?.trim() ? rfqDetail.incotermsSnapshot.trim() : null
              }
            />
          )}

        {canPerformProcurementStaffActions(auth.role) &&
          !ro &&
          (selectedRequest.status === 'procurementReview' || selectedRequest.status === 'sourcing') && (
            <div className="panel-actions">
              <h4>Return to requestor</h4>
              <p className="hint">
                Reject sends the request back to the requestor in Rejected status. They can edit and resubmit.
              </p>
              <label htmlFor="procurement-reject-comment" className="sr-only">
                Comment (optional)
              </label>
              <textarea
                id="procurement-reject-comment"
                placeholder="Optional comment for the requestor…"
                value={procurementRejectComment}
                onChange={(e) => setProcurementRejectComment(e.target.value)}
                rows={2}
              />
              <button
                type="button"
                className="danger"
                disabled={procurementRejectSaving}
                onClick={() => {
                  void (async () => {
                    setProcurementRejectSaving(true);
                    try {
                      await onRejectProcurement(
                        selectedRequest.id,
                        procurementRejectComment.trim() || undefined
                      );
                      setProcurementRejectComment('');
                    } finally {
                      setProcurementRejectSaving(false);
                    }
                  })();
                }}
              >
                {procurementRejectSaving ? 'Rejecting…' : 'Reject to requestor'}
              </button>
            </div>
          )}

        {canPerformProcurementStaffActions(auth.role) && !ro && selectedRequest.status === 'orderPlaced' && (
          <div className="panel-actions">
            <p className="hint">Confirm goods or services have been procured.</p>
            <button type="button" onClick={() => void onMarkProcured(selectedRequest.id)}>
              Mark procured
            </button>
          </div>
        )}

        {canPerformProcurementStaffActions(auth.role) && !ro && selectedRequest.status === 'procured' && (
          <div className="panel-actions">
            {selectedRequest.fulfilledByMaterialTransferId ? (
              <>
                <p className="hint">
                  This request is Procured because transfer{' '}
                  <Link
                    to={`/material-transfers/${encodeURIComponent(selectedRequest.fulfilledByMaterialTransferId)}/edit`}
                  >
                    {selectedRequest.fulfilledByMaterialTransferNumber ?? 'linked'}
                  </Link>{' '}
                  fully covers it. Attach the movement ticket on a planned transfer to mark this request Complete. Use
                  Mark complete only for older transfers that already moved stock.
                </p>
                <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                  <Link
                    to={`/material-transfers/${encodeURIComponent(selectedRequest.fulfilledByMaterialTransferId)}/edit`}
                  >
                    Open the transfer
                  </Link>
                  <button type="button" className="secondary" onClick={() => void onMarkComplete(selectedRequest.id)}>
                    Mark complete
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="hint">Close the request when procurement is finished.</p>
                <button type="button" onClick={() => void onMarkComplete(selectedRequest.id)}>
                  Mark complete
                </button>
              </>
            )}
          </div>
        )}
        </PagerSection>

        <PagerSection id="notes" title="Notes">
        <div className="panel-actions">
          <h4>Request notes</h4>
          <p className="hint">Shared with everyone who can open this request. Newest additions appear at the bottom.</p>
          <ul className="collaboration-notes-list">
            {sortedCollaborationNotes.length === 0 && (
              <li className="collaboration-notes-empty">No notes yet.</li>
            )}
            {sortedCollaborationNotes.map((n) => (
              <li key={n.id} className="collaboration-note-card">
                <div className="collaboration-note-meta">
                  <strong>{n.authorDisplayName?.trim() || n.authorUserId || 'User'}</strong>
                  <span className="collaboration-note-when">
                    {n.createdAtUtc ? new Date(n.createdAtUtc).toLocaleString() : ''}
                  </span>
                </div>
                <div className="collaboration-note-body">{n.body}</div>
              </li>
            ))}
          </ul>
          {!ro && (
            <>
              <label htmlFor="new-request-note" className="sr-only">
                Add a note
              </label>
              <textarea
                id="new-request-note"
                placeholder="Add a note for the team…"
                value={newNoteDraft}
                onChange={(e) => setNewNoteDraft(e.target.value)}
                rows={3}
              />
              <button type="button" disabled={noteSaving || !newNoteDraft.trim()} onClick={() => void handleAddNote()}>
                {noteSaving ? 'Adding…' : 'Add note'}
              </button>
            </>
          )}
        </div>
        </PagerSection>
        </SectionPager>
      </div>
    </aside>
  );
}
